"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Loader2, X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import DefaultButton from "../DefaultButton";

interface ConfirmDeleteModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onConfirm: () => void | Promise<void>;
  title?: string;
  message?: string;
  confirmText?: string;
  isLoading?: boolean;
}

export default function ConfirmDeleteModal({
  open,
  setOpen,
  onConfirm,
  title = "Confirmar exclusão",
  message = "Tem certeza que deseja excluir estes dados?",
  confirmText = "Excluir",
  isLoading = false,
}: ConfirmDeleteModalProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const modal = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="confirm-delete-backdrop"
            role="presentation"
            className="fixed inset-0 z-[200] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            onClick={() => {
              if (!isLoading) setOpen(false);
            }}
          />
          <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              key="confirm-delete-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-delete-title"
              className="pointer-events-auto relative w-full max-w-md rounded-card bg-white p-6 shadow-lg"
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.88 }}
              transition={{
                type: "spring",
                damping: 28,
                stiffness: 360,
                mass: 0.85,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3">
                <h2
                  id="confirm-delete-title"
                  className="min-w-0 flex-1 text-left text-lede font-bold leading-tight text-ink"
                >
                  {title}
                </h2>
                <button
                  id="btn-confirm-delete-close"
                  type="button"
                  className="shrink-0 rounded-full p-2 text-slate transition-colors hover:bg-ground hover:text-ink disabled:pointer-events-none"
                  aria-label="Fechar"
                  disabled={isLoading}
                  onClick={() => setOpen(false)}
                >
                  <X className="h-5 w-5" strokeWidth={2} />
                </button>
              </div>
              <p className="mt-6 text-body text-slate">{message}</p>

              <div className="mt-6 flex w-full flex-row flex-wrap justify-end gap-3">
                <DefaultButton
                  id="btn-confirm-delete-cancel"
                  text="Cancelar"
                  bgColor="transparent"
                  textColor="#000000"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                  className="!w-auto !px-6 !py-3.5 border border-line"
                />
                <DefaultButton
                  id="btn-confirm-delete-confirm"
                  text={
                    <span className="relative inline-block align-middle">
                      <span
                        className={isLoading ? "invisible" : undefined}
                        aria-hidden={isLoading}
                      >
                        {confirmText}
                      </span>
                      {isLoading && (
                        <span
                          className="pointer-events-none absolute inset-0 flex items-center justify-center"
                          aria-hidden
                        >
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                        </span>
                      )}
                    </span>
                  }
                  bgColor={isLoading ? "#9CA3AF" : "#DC2626"}
                  textColor="white"
                  onClick={() => {
                    if (!isLoading) {
                      onConfirm();
                    }
                  }}
                  disabled={isLoading}
                  className="!w-auto !px-6 !py-3.5"
                />
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return null;

  return createPortal(modal, document.body);
}
